import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ExerciseLibraryLine from "./ExerciseLibraryLine";
import SvgIcon from "../../../parts/SvgIcon";
import PrimaryDropdown from "../../../parts/primary-dropdown";
import submit from "submit-form";
import ToolTip from "../../../parts/tooltip";
import ComplexLibraryLine from "./ComplexLibraryLine";

const exerciseOptionKeys = {
  squat: "addSquatVariation",
  bench: "addBenchVariation",
  deadlift: "addDeadliftVariation",
  clean: "addCleanVariation",
  jerk: "addJerkVariation",
  snatch: "addSnatchVariation",
  overheadPress: "addOverheadPressVariation",
  other: "addOtherVariation",
  complex: "addComplex",
};

function ExerciseLibraryCoachComponent({
  type,
  primary,
  secondaries,
  activeUserToRequest,
  isAdminConsole = false,
}) {
  const { t } = useTranslation("coaching-portal");
  const [search, setSearch] = useState("");

  const movements = useMemo(
    () => ({
      squat: t("exerciseLibraryScreen.movements.squat"),
      bench: t("exerciseLibraryScreen.movements.bench"),
      deadlift: t("exerciseLibraryScreen.movements.deadlift"),
      clean: t("exerciseLibraryScreen.movements.clean"),
      jerk: t("exerciseLibraryScreen.movements.jerk"),
      snatch: t("exerciseLibraryScreen.movements.snatch"),
      overheadPress: t("exerciseLibraryScreen.movements.overheadPress"),
      other: t("exerciseLibraryScreen.movements.other"),
    }),
    [t]
  );

  const handleSearch = useCallback(
    (event) => setSearch(event.target.value),
    []
  );

  const exercises = useMemo(() => {
    if (!secondaries) return [];
    if (!search) return secondaries;

    return secondaries.filter(({ movement }) => {
      return String(movement)
        .toLowerCase()
        .includes(search.toLowerCase().trim());
    });
  }, [secondaries, search]);

  const showPrimary = useMemo(
    () =>
      !["assistant", "other", "complex"].includes(primary) &&
      !isAdminConsole &&
      (!search ||
        (search &&
          primary.toLowerCase().includes(search.toLowerCase().trim()))),
    [primary, search, isAdminConsole]
  );

  const primaryLabel =
    (exerciseOptionKeys[primary] &&
      t(`exerciseLibraryScreen.${exerciseOptionKeys[primary]}`)) ||
    t("exerciseLibraryScreen.accessoryLabel");

  const addLabel = t("exerciseLibraryScreen.add");

  const dropdownOptions = useMemo(
    () =>
      [
        { label: `${addLabel} ${primaryLabel}`, value: primary || "assistant" },
        ...Object.entries(exerciseOptionKeys).map(([key, keyName]) => ({
          label: `${addLabel} ${t(`exerciseLibraryScreen.${keyName}`)}`,
          value: key,
        })),
        {
          label: `${addLabel} ${t("exerciseLibraryScreen.addAccessory")}`,
          value: "assistant",
        },
      ].filter(
        (option, index, self) =>
          self.findIndex((o) => o.label === option.label) === index
      ),
    [t, primaryLabel, primary, addLabel]
  );

  const handleOpenModal = useCallback((value) => {
    if (value) {
      let body = {
        "modal[type]": "add-secondary-movement",
        "modal[primary]": value,
      };
      if (value === "assistant") {
        body = { "modal[type]": "add-other-exercise" };
      } else if (value === "complex") {
        body = { "modal[type]": "add-complex" };
      }

      submit("/actions/refresh", {
        method: "GET",
        body,
      });
    }
  }, []);

  return (
    <main className="ex-library-wrapper">
      <header>
        <hgroup className="mobile-hidden">
          <h3>
            {isAdminConsole ? t("exerciseLibraryScreen.masterTitle") : ""}{" "}
            {t("exerciseLibraryScreen.pageTitle")}
          </h3>
          {!isAdminConsole && (
            <p className="p4">{t("exerciseLibraryScreen.description")}</p>
          )}
        </hgroup>

        <hgroup>
          {!isAdminConsole && (
            <a
              role="button"
              data-button-type="outline"
              href="/coaching-portal/exercise-library/catalog"
            >
              {t("exerciseLibraryScreen.addFromCatalog")}
            </a>
          )}
          <PrimaryDropdown
            summaryText={t("exerciseLibraryScreen.addCustomExercise")}
            options={dropdownOptions}
            onSelectFn={handleOpenModal}
            fullOption={false}
            alignRight={true}
            isAdminConsole={isAdminConsole}
          />
        </hgroup>
      </header>

      <section>
        <div>
          <input
            type="search"
            name="search"
            placeholder={t("exerciseLibraryScreen.searchPlaceholder")}
            value={search}
            onChange={handleSearch}
            autoComplete="off"
            data-expandable
            className={search.length ? "expanded" : ""}
          />
          <div className="flex items-center">
            <hgroup>
              <p className="mb-2">
                {t("exerciseLibraryScreen.customExercise")}
              </p>
              <ToggleLinksInput
                className="tabs inline-tabs"
                name="primary"
                options={[
                  {
                    text: t("exerciseLibraryScreen.accessories"),
                    value: "assistant",
                  },
                ]}
              />
            </hgroup>
            <hgroup>
              <div className="row">
                <p>{t("exerciseLibraryScreen.powerlifting")}</p>
                <ToolTip
                  id="powerlifting"
                  body={t("exerciseLibraryScreen.powerliftingTooltip")}
                >
                  <span className="tooltip-icon">?</span>
                </ToolTip>
              </div>
              <ToggleLinksInput
                className="tabs inline-tabs"
                name="primary"
                options={[
                  { text: t("exerciseLibraryScreen.squat"), value: "squat" },
                  { text: t("exerciseLibraryScreen.bench"), value: "bench" },
                  {
                    text: t("exerciseLibraryScreen.deadlift"),
                    value: "deadlift",
                  },
                ]}
              />
            </hgroup>
            <hgroup>
              <div className="row">
                <p>{t("exerciseLibraryScreen.olympicWeightlifting")}</p>
                <ToolTip
                  id="olympic-weightlifting"
                  body={t("exerciseLibraryScreen.olympicTooltip")}
                >
                  <span className="tooltip-icon">?</span>
                </ToolTip>
              </div>
              <ToggleLinksInput
                className="tabs inline-tabs"
                name="primary"
                options={[
                  { text: t("exerciseLibraryScreen.clean"), value: "clean" },
                  { text: t("exerciseLibraryScreen.jerk"), value: "jerk" },
                  { text: t("exerciseLibraryScreen.snatch"), value: "snatch" },
                  {
                    text: t("exerciseLibraryScreen.complexes"),
                    value: "complex",
                  },
                ]}
              />
            </hgroup>
            <hgroup>
              <div className="row">
                <p>{t("exerciseLibraryScreen.strongmanOther")}</p>
                <ToolTip
                  id="strongman"
                  body={t("exerciseLibraryScreen.strongmanTooltip")}
                >
                  <span className="tooltip-icon">?</span>
                </ToolTip>
              </div>
              <ToggleLinksInput
                className="tabs inline-tabs"
                name="primary"
                options={[
                  {
                    text: t("exerciseLibraryScreen.overheadPress"),
                    value: "overheadPress",
                  },
                  { text: t("exerciseLibraryScreen.other"), value: "other" },
                ]}
              />
            </hgroup>
          </div>
        </div>
        <section
          role="table-wrapper"
          data-scrollable
          className="table-exercise-library"
        >
          <table>
            <thead>
              <tr>
                <th />
                <th>{t("exerciseLibraryScreen.exerciseName")}</th>
                <th>{t("exerciseLibraryScreen.video")}</th>
                {primary !== "complex" && (
                  <>
                    <th>{t("exerciseLibraryScreen.instruction")}</th>
                    <th>{t("exerciseLibraryScreen.primaryMuscle")}</th>
                    <th>{t("exerciseLibraryScreen.secondaryMuscle")}</th>
                    <th>{t("exerciseLibraryScreen.exerciseType")}</th>
                  </>
                )}
                <th>{t("exerciseLibraryScreen.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {showPrimary && (
                <ExerciseLibraryLine
                  secondary={Object.assign(
                    {
                      movement: movements[primary] || primary,
                      primary,
                    },
                    activeUserToRequest.primaryExercises?.[primary]
                  )}
                  primaryItem
                />
              )}
              {exercises?.length ? (
                <>
                  {exercises.map((secondary, i) => {
                    if (primary === "complex") {
                      return (
                        <ComplexLibraryLine
                          complex={secondary}
                          key={secondary._id}
                        />
                      );
                    }
                    return (
                      <ExerciseLibraryLine
                        secondary={secondary}
                        key={`${secondary.primary}_${i}`}
                        isAdminConsole={isAdminConsole}
                        primary={primary}
                      />
                    );
                  })}
                </>
              ) : null}
            </tbody>
          </table>
          {(!exercises || exercises.length === 0) && !showPrimary && (
            <EmptyView search={search} t={t} />
          )}
          <div
            className="dashed-box add-new-exercise force-mobile-hidden"
            onClick={() => {
              handleOpenModal(primary);
            }}
          >
            <SvgIcon src="/img/new-icons/add-square-outline.svg" alt="back" />
            <span>{t("exerciseLibraryScreen.addNewExercise")}</span>
          </div>
        </section>
      </section>
    </main>
  );
}

function EmptyView({ search, t }) {
  const filter = search
    ? t("exerciseLibraryScreen.noExercisesFoundFilter")
    : "";
  return (
    <div className="empty-state">
      <p className="s1">
        {t("exerciseLibraryScreen.noExercisesFound", { filter })}
      </p>
    </div>
  );
}

export default ExerciseLibraryCoachComponent;
